import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import StartGameOverlay from './StartGameOverlay';
import RetroLoadingDots from './RetroLoadingDots';

const RetroLetterCatcher = ({ 
  isActive = false, 
  onEnd,
  onGameComplete,
  showStartButton = false,
  isGenerating = false 
}) => {
  const [letters, setLetters] = useState([]);
  const [score, setScore] = useState(0);
  const [playerX, setPlayerX] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  // Show start button only when explicitly requested (like in RetroArcade)
  const [waitingToStart, setWaitingToStart] = useState(showStartButton);
  const containerRef = useRef(null);

  const colors = [
    '#FF00FF', '#FF6B00', '#00FF9F', '#FF1493',
    '#FFB800', '#00FFE0', '#FF4D00', '#FF8A00'
  ];

  // Initialize game
  useEffect(() => {
    if (isActive && !gameStarted && !waitingToStart) {
      const { width } = containerRef.current.getBoundingClientRect();
      setPlayerX(width / 2 - 25);
      setGameStarted(true);
      setScore(0);
      setTimeLeft(10);
      setLetters([]);
    }
  }, [isActive, waitingToStart]);

  // Game timer - only runs when game has actually started
  useEffect(() => {
    if (!gameStarted || waitingToStart) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onGameComplete?.({
            type: 'lettercatcher',
            score,
            title: 'Letter Catcher Results',
            message: `You caught ${score} letters!`
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameStarted, waitingToStart, score]);

  const handleStart = () => {
    setWaitingToStart(false);
  };

  // Handle mouse/touch movement
  useEffect(() => {
    if (!containerRef.current || !gameStarted) return;

    const handleMouseMove = (e) => {
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - 25;
      setPlayerX(Math.max(0, Math.min(x, rect.width - 50)));
    };

    containerRef.current.addEventListener('mousemove', handleMouseMove);
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, [gameStarted]);

  // Game loop
  useEffect(() => {
    if (!gameStarted) return;

    let animationFrameId;
    const spawnInterval = setInterval(() => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setLetters(prev => [...prev, {
          id: Date.now(),
          x: Math.random() * (width - 30),
          y: -30,
          char: String.fromCharCode(65 + Math.floor(Math.random() * 26)),
          color: colors[Math.floor(Math.random() * colors.length)],
          speed: 2 + Math.random() * 2
        }]);
      }
    }, 1000);

    const updateGame = () => {
      setLetters(prev => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return prev;

        return prev.filter(letter => {
          // Check for collision with paddle
          if (letter.y > rect.height - 60 && letter.y < rect.height - 40) {
            const paddleLeft = playerX;
            const paddleRight = playerX + 50;
            if (letter.x > paddleLeft && letter.x < paddleRight) {
              setScore(s => s + 1);
              return false;
            }
          }
          // Remove if past bottom
          if (letter.y > rect.height) return false;
          return true;
        }).map(letter => ({
          ...letter,
          y: letter.y + letter.speed
        }));
      });

      animationFrameId = requestAnimationFrame(updateGame);
    };

    animationFrameId = requestAnimationFrame(updateGame);

    return () => {
      clearInterval(spawnInterval);
      cancelAnimationFrame(animationFrameId);
    };
  }, [gameStarted, playerX]);

  return (
    <motion.div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden rounded-xl cursor-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      <AnimatePresence>
        {waitingToStart ? (
          <StartGameOverlay 
            onStart={handleStart}
            gameName="Letter Catcher"
          />
        ) : (
          <>
            {/* Score and Timer */}
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
              <div className="text-white font-['VT323'] text-3xl">
                {score}
              </div>
              <div className="text-white font-['VT323'] text-3xl">
                {timeLeft}s
              </div>
            </div>

            {/* Falling Letters */}
            {letters.map((letter) => (
              <motion.div
                key={letter.id}
                className="absolute font-['VT323'] text-2xl"
                style={{
                  color: letter.color,
                  x: letter.x,
                  y: letter.y,
                  textShadow: `0 0 10px ${letter.color}`
                }}
              >
                {letter.char}
              </motion.div>
            ))}

            {/* Player Paddle */}
            <motion.div
              className="absolute bottom-4 h-8 w-12 rounded-lg bg-white/80"
              style={{ x: playerX }}
              animate={{ x: playerX }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            />
          </>
        )}
      </AnimatePresence>


    </motion.div>
  );
};

export default RetroLetterCatcher; 